import type { JSX, ReactNode } from "react";

import { t } from "@impero-com/common/utils/i18n";
import {
    ActionMenu,
    ActionSpec,
    PageNavMenu,
    PageNavMenuItem,
    PageNavMenuSection,
} from "@impero-com/ui";

import { PageNavMenuLayout, PageNavMenuMain } from "../../../components/layout";
import { WebRoute } from "../../../routes";

import type { ReportListPageType } from "./layout";
import type { ReportsCount } from "./navigation";

interface ReportsMenuLayoutProps {
    pageType: ReportListPageType;
    reportsCount: ReportsCount;
    actions: Array<ActionSpec>;
    children: ReactNode;
}

function ReportsMenuLayout({
    pageType,
    reportsCount,
    actions,
    children,
}: ReportsMenuLayoutProps): JSX.Element {
    return (
        <PageNavMenuLayout
            title={t("Report_BreadCrumb")}
            menu={
                <ReportsMenu pageType={pageType} reportsCount={reportsCount} />
            }
        >
            <PageNavMenuMain
                actions={
                    <ActionMenu id="action-menu-reports" actions={actions} />
                }
            >
                {children}
            </PageNavMenuMain>
        </PageNavMenuLayout>
    );
}

interface ReportsMenuProps {
    pageType: ReportListPageType;
    reportsCount: ReportsCount;
}

function ReportsMenu({ pageType, reportsCount }: ReportsMenuProps) {
    return (
        <PageNavMenu aria-label={t("Report_BreadCrumb")}>
            <PageNavMenuSection label={t("Report_BreadCrumb")}>
                {reportViewRows(reportsCount).map((row) => (
                    <PageNavMenuItem
                        key={row.pageType}
                        label={row.label}
                        to={row.to}
                        count={row.count === 0 ? undefined : row.count}
                        active={row.pageType === pageType}
                    />
                ))}
            </PageNavMenuSection>
        </PageNavMenu>
    );
}

interface ReportViewRow {
    pageType: ReportListPageType;
    label: string;
    to: string;
    count: number;
}

function reportViewRows(reportsCount: ReportsCount): Array<ReportViewRow> {
    return [
        {
            pageType: "All",
            label: t("Report_BreadCrumb_All"),
            to: WebRoute.REPORTS,
            count: reportsCount.all,
        },
        {
            pageType: "Status",
            label: t("Report_BreadCrumb_Status"),
            to: WebRoute.REPORTS_STATUS,
            count: reportsCount.status,
        },
        {
            pageType: "Classification",
            label: t("Report_BreadCrumb_Classification"),
            to: WebRoute.REPORTS_CLASSIFICATION,
            count: reportsCount.classification,
        },
        {
            pageType: "Workload",
            label: t("Report_BreadCrumb_Workload"),
            to: WebRoute.REPORTS_WORKLOAD,
            count: reportsCount.workload,
        },
        {
            pageType: "Datasheet",
            label: t("Report_BreadCrumb_Datasheet"),
            to: WebRoute.REPORTS_DATASHEET,
            count: reportsCount.datasheet,
        },
    ];
}

export { ReportsMenuLayout };
